import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  BarChart3, Filter, Calendar, Building2, RefreshCw,
  ChevronDown, ChevronUp, Info, Euro, Activity,
  AlertCircle, XCircle, ArrowUpRight, ArrowDownRight,
  Package, Layers, UploadCloud
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine
} from "recharts";
import { toast } from "sonner";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const ANOM_TYPE_LABELS = {
  not_in_catalog: { label: "Fuera de catálogo", color: "text-purple-700 bg-purple-50 border-purple-200" },
  excess_journeys: { label: "Exceso de jornadas", color: "text-red-600 bg-red-50 border-red-200" },
  deficit_journeys: { label: "Déficit de jornadas", color: "text-amber-600 bg-amber-50 border-amber-200" },
  wrong_category: { label: "Categoría incorrecta", color: "text-blue-600 bg-blue-50 border-blue-200" },
};

const SEVERITY_CONFIG = {
  high:   { label: "Alta",   bg: "bg-red-50",    text: "text-red-700",    border: "border-red-200",   dot: "bg-red-500" },
  medium: { label: "Media",  bg: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-200", dot: "bg-amber-500" },
  low:    { label: "Baja",   bg: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200",  dot: "bg-blue-400" },
  ok:     { label: "Normal", bg: "bg-emerald-50",text: "text-emerald-700",border: "border-emerald-200",dot: "bg-emerald-500" },
};

function fmt(n, dec = 0) {
  if (n == null) return "—";
  return n.toLocaleString("es-ES", { maximumFractionDigits: dec, minimumFractionDigits: dec });
}

function fmtEur(n, dec = 0) {
  if (n == null) return "—";
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: dec });
}

function DevBadge({ pct, compact = false }) {
  if (pct == null) return <span className="text-slate-300 text-xs">—</span>;
  const abs = Math.abs(pct);
  const color = abs < 10 ? "text-emerald-600" : abs < 25 ? "text-amber-600" : "text-red-600";
  const Icon = pct > 0 ? ArrowUpRight : pct < 0 ? ArrowDownRight : null;
  return (
    <span className={`flex items-center gap-0.5 font-bold text-sm ${color}`}>
      {Icon && <Icon size={13} />}
      {pct > 0 ? "+" : ""}{fmt(pct, 1)}%
    </span>
  );
}

function KpiCard({ icon: Icon, label, planned, actual, deviation, unit = "", devPct, color = "#007934", loading }) {
  const sev = devPct != null
    ? Math.abs(devPct) < 10 ? "ok" : Math.abs(devPct) < 25 ? "medium" : "high"
    : "ok";
  const cfg = SEVERITY_CONFIG[sev];

  return (
    <div className={`card-white p-5 border-l-4 ${sev === "ok" ? "border-[#007934]" : sev === "medium" ? "border-amber-400" : "border-red-400"}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${sev === "ok" ? "bg-[#E6F4EA]" : sev === "medium" ? "bg-amber-50" : "bg-red-50"}`}>
          <Icon size={17} className={sev === "ok" ? "text-[#007934]" : sev === "medium" ? "text-amber-600" : "text-red-500"} />
        </div>
        {devPct != null && <DevBadge pct={devPct} />}
      </div>
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{label}</p>
      {loading ? (
        <div className="h-10 bg-slate-100 animate-pulse rounded-lg" />
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-slate-900">{actual}</span>
            <span className="text-xs text-slate-400">{unit}</span>
          </div>
          {planned != null && (
            <p className="text-xs text-slate-400 mt-1">
              Previsto: <span className="font-semibold text-slate-600">{planned}</span>
              {deviation != null && deviation !== 0 && (
                <span className={`ml-2 font-semibold ${deviation > 0 ? "text-red-500" : "text-emerald-600"}`}>
                  ({deviation > 0 ? "+" : ""}{fmtEur(deviation)})
                </span>
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default function Anomalies() {
  const { authAxios } = useAuth();
  const now = new Date();
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [severityFilter, setSeverityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showAllServices, setShowAllServices] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    authAxios.get("/contracts").then(r => {
      setContracts(r.data);
      if (r.data.length > 0) setContractId(r.data[0].id);
    });
  }, []);

  const fetchData = useCallback(async () => {
    if (!contractId || !selectedMonth) return;
    setLoading(true);
    setError(null);
    try {
      const r = await authAxios.get(`/anomalies?contract_id=${contractId}&month=${selectedMonth}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Error cargando los datos");
    } finally {
      setLoading(false);
    }
  }, [contractId, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const contract = contracts.find(c => c.id === contractId);

  const filteredAnomalies = (data?.anomalies || []).filter(a => {
    if (severityFilter !== "all" && a.severity !== severityFilter) return false;
    if (typeFilter !== "all" && !a.anomaly_types.includes(typeFilter)) return false;
    return true;
  });

  const chartData = (data?.service_comparison || [])
    .filter(s => s.planned_journeys > 0 || s.actual_journeys > 0)
    .sort((a, b) => Math.abs(b.deviation_pct) - Math.abs(a.deviation_pct))
    .slice(0, 15)
    .map(s => ({
      name: s.service_name.length > 22 ? s.service_name.slice(0, 22) + "…" : s.service_name,
      full: s.service_name,
      shift: s.shift,
      planned: Math.round(s.planned_journeys),
      actual: s.actual_journeys,
      dev: s.deviation_pct,
    }));

  const monthLabel = (() => {
    const [y, m] = selectedMonth.split("-");
    return `${MONTHS[parseInt(m) - 1]} ${y}`;
  })();

  const s = data?.summary;

  return (
    <div className="p-6 space-y-5 max-w-screen-xl mx-auto">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap animate-fade-in-up">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={20} className="text-amber-500" />
            <h1 className="text-2xl font-black text-slate-900">Análisis Ofertado vs. Ejecutado</h1>
          </div>
          <p className="text-slate-400 text-sm">Compara las condiciones económicas del contrato con la ejecución real de los partes</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Contract selector */}
          <select
            data-testid="anomalies-contract-select"
            value={contractId}
            onChange={e => setContractId(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20 font-semibold"
          >
            {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          {/* Month picker */}
          <input
            data-testid="anomalies-month-input"
            type="month"
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
          />

          <button
            data-testid="anomalies-refresh-btn"
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* No plan warning */}
      {!loading && data && !data.has_plan && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 animate-fade-in-up">
          <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">Sin condiciones económicas importadas para {contract?.name}</p>
            <p className="text-sm text-amber-700 mt-0.5">
              Ve a <strong>Administración → Catálogo de Servicios</strong> y sube el Excel de condiciones económicas para habilitar el análisis completo.
              Con los partes actuales puedes ver las jornadas ejecutadas, pero no la comparativa de costes.
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-3">
          <XCircle size={16} className="text-red-500" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in-up">
        <KpiCard
          icon={Activity}
          label="Jornadas ejecutadas"
          planned={s ? fmt(s.total_planned_journeys, 0) : null}
          actual={s ? fmt(s.total_actual_journeys) : "—"}
          devPct={s?.journey_deviation_pct}
          unit="jornadas"
          loading={loading}
        />
        <KpiCard
          icon={Euro}
          label="Coste ejecutado"
          planned={s ? fmtEur(s.total_planned_cost) : null}
          actual={s ? fmtEur(s.total_actual_cost) : "—"}
          deviation={s?.cost_deviation}
          devPct={s?.cost_deviation_pct}
          unit=""
          loading={loading}
        />
        <KpiCard
          icon={AlertTriangle}
          label="Anomalías detectadas"
          actual={s ? fmt(s.anomaly_count) : "—"}
          unit={`(${s?.high_severity || 0} alta · ${s?.medium_severity || 0} media)`}
          devPct={null}
          loading={loading}
        />
        <KpiCard
          icon={Package}
          label="Servicios fuera de catálogo"
          actual={s ? fmt(s.not_in_catalog) : "—"}
          unit="servicios no planificados"
          devPct={null}
          loading={loading}
        />
      </div>

      {/* ── Chart: top deviations ── */}
      {!loading && chartData.length > 0 && (
        <div className="card-white p-5 animate-fade-in-up">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <BarChart3 size={16} className="text-slate-500" />
              Top 15 servicios con mayor desviación — {monthLabel}
            </h2>
            <span className="text-xs text-slate-400">{contract?.name}</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10, fill: "#94a3b8" }}
                angle={-35}
                textAnchor="end"
                interval={0}
              />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <Tooltip
                formatter={(val, name) => [val, name === "planned" ? "Previsto" : "Ejecutado"]}
                labelFormatter={(label, p) => p?.[0]?.payload?.full || label}
                contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #e2e8f0" }}
              />
              <ReferenceLine y={0} stroke="#e2e8f0" />
              <Bar dataKey="planned" name="Previsto" fill="#E6F4EA" stroke="#007934" strokeWidth={1} radius={[3, 3, 0, 0]} />
              <Bar dataKey="actual" name="Ejecutado" radius={[3, 3, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry.dev > 15 ? "#ef4444" : entry.dev < -15 ? "#f59e0b" : "#007934"}
                    fillOpacity={0.8}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Anomaly List ── */}
      {!loading && (
        <div className="card-white overflow-hidden animate-fade-in-up">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" />
              Anomalías detectadas — {monthLabel}
              {filteredAnomalies.length > 0 && (
                <span className="text-xs bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-full ml-1">
                  {filteredAnomalies.length}
                </span>
              )}
            </h2>

            {/* Filters */}
            <div className="flex items-center gap-2">
              <select
                data-testid="severity-filter"
                value={severityFilter}
                onChange={e => setSeverityFilter(e.target.value)}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none"
              >
                <option value="all">Todas las severidades</option>
                <option value="high">Alta</option>
                <option value="medium">Media</option>
                <option value="low">Baja</option>
              </select>
              <select
                data-testid="type-filter"
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none"
              >
                <option value="all">Todos los tipos</option>
                <option value="not_in_catalog">Fuera de catálogo</option>
                <option value="excess_journeys">Exceso jornadas</option>
                <option value="deficit_journeys">Déficit jornadas</option>
                <option value="wrong_category">Categoría incorrecta</option>
              </select>
            </div>
          </div>

          {filteredAnomalies.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <CheckCircle2 size={32} className="mx-auto mb-3 text-emerald-400 opacity-60" />
              <p className="font-semibold text-slate-600">
                {data?.anomalies?.length === 0 ? "¡Sin anomalías en este período!" : "No hay anomalías con este filtro"}
              </p>
              <p className="text-sm mt-1">
                {data?.summary?.total_reports === 0
                  ? "No hay partes registrados en este período"
                  : "Los servicios ejecutados coinciden con el plan contratado"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide w-7"></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Servicio</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Turno</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Jorn. Prev.</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Jorn. Real</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide">Desv. %</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Coste Prev.</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Coste Real</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide">Desv. €</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Tipo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAnomalies.map((a, i) => {
                    const cfg = SEVERITY_CONFIG[a.severity];
                    const isExp = expandedRow === i;
                    return (
                      <>
                        <tr
                          key={i}
                          data-testid={`anomaly-row-${i}`}
                          className={`cursor-pointer transition-colors hover:bg-slate-50 ${isExp ? "bg-slate-50" : ""}`}
                          onClick={() => setExpandedRow(isExp ? null : i)}
                        >
                          <td className="px-4 py-3">
                            <span className={`inline-block w-2 h-2 rounded-full ${cfg.dot}`} />
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-800 text-xs max-w-[200px] truncate" title={a.service_name}>
                              {a.service_name}
                            </p>
                            {!a.in_catalog && (
                              <span className="text-[10px] text-purple-600 font-semibold">No está en el catálogo</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border
                              ${a.shift === "MAÑANA" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                a.shift === "TARDE" ? "bg-orange-50 text-orange-700 border-orange-200" :
                                "bg-slate-100 text-slate-600 border-slate-200"}`}>
                              {a.shift}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs text-slate-500">{fmt(a.planned_journeys, 1)}</td>
                          <td className="px-4 py-3 text-right font-mono text-xs font-bold text-slate-800">{a.actual_journeys}</td>
                          <td className="px-4 py-3 text-right"><DevBadge pct={a.deviation_pct} /></td>
                          <td className="px-4 py-3 text-right text-xs text-slate-500 font-mono whitespace-nowrap">{fmtEur(a.planned_cost)}</td>
                          <td className="px-4 py-3 text-right text-xs font-bold text-slate-800 font-mono whitespace-nowrap">{fmtEur(a.actual_cost)}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span className={`text-xs font-bold ${a.cost_deviation > 0 ? "text-red-600" : a.cost_deviation < 0 ? "text-emerald-600" : "text-slate-400"}`}>
                              {a.cost_deviation > 0 ? "+" : ""}{fmtEur(a.cost_deviation)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {a.anomaly_types.map(t => {
                                const tl = ANOM_TYPE_LABELS[t] || { label: t, color: "text-slate-600 bg-slate-100 border-slate-200" };
                                return (
                                  <span key={t} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${tl.color}`}>
                                    {tl.label}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                        {isExp && (
                          <tr key={`exp-${i}`} className="bg-slate-50/80">
                            <td colSpan={10} className="px-6 py-3 text-xs text-slate-600 border-b border-slate-100">
                              <div className="flex items-center gap-6 flex-wrap">
                                <span><strong>Días con actividad:</strong> {a.active_days}</span>
                                <span><strong>OK:</strong> {a.ok_count} · <strong>KO:</strong> {a.ko_count}</span>
                                <span className={`font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
                                  Severidad: {cfg.label}
                                </span>
                                {!a.in_catalog && (
                                  <span className="text-purple-600">
                                    ⚠ Servicio ejecutado que no aparece en las condiciones del contrato
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Full service comparison ── */}
      {!loading && data && (
        <div className="card-white overflow-hidden animate-fade-in-up">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
              <Layers size={15} className="text-slate-500" />
              Comparativa completa de servicios — {monthLabel}
            </h2>
            <button
              data-testid="toggle-all-services-btn"
              onClick={() => setShowAllServices(!showAllServices)}
              className="text-xs text-[#007934] hover:underline font-semibold flex items-center gap-1"
            >
              {showAllServices ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {showAllServices ? "Ocultar" : "Mostrar todos"}
            </button>
          </div>

          {showAllServices && (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-slate-400 uppercase tracking-wide">Servicio</th>
                    <th className="px-4 py-2 text-left font-semibold text-slate-400 uppercase tracking-wide">Turno</th>
                    <th className="px-4 py-2 text-right font-semibold text-slate-400 uppercase tracking-wide">Previsto</th>
                    <th className="px-4 py-2 text-right font-semibold text-slate-400 uppercase tracking-wide">Real</th>
                    <th className="px-4 py-2 text-right font-semibold text-slate-400 uppercase tracking-wide">Desv %</th>
                    <th className="px-4 py-2 text-right font-semibold text-slate-400 uppercase tracking-wide">€ Prev.</th>
                    <th className="px-4 py-2 text-right font-semibold text-slate-400 uppercase tracking-wide">€ Real</th>
                    <th className="px-4 py-2 text-right font-semibold text-slate-400 uppercase tracking-wide">Desv €</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.service_comparison || []).map((s, i) => {
                    const cfg = SEVERITY_CONFIG[s.severity];
                    return (
                      <tr key={i} className={`hover:bg-slate-50 ${s.severity !== "ok" ? cfg.bg + "/30" : ""}`}>
                        <td className="px-4 py-2 font-medium text-slate-700 max-w-xs">
                          <p className="truncate" title={s.service_name}>{s.service_name}</p>
                          {!s.in_catalog && <span className="text-[10px] text-purple-500 font-semibold">Fuera de catálogo</span>}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border
                            ${s.shift === "MAÑANA" ? "bg-amber-50 text-amber-700 border-amber-200" :
                              s.shift === "TARDE" ? "bg-orange-50 text-orange-700 border-orange-200" :
                              "bg-slate-100 text-slate-600 border-slate-200"}`}>
                            {s.shift}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-slate-500">{fmt(s.planned_journeys, 1)}</td>
                        <td className="px-4 py-2 text-right font-mono font-bold text-slate-800">{s.actual_journeys}</td>
                        <td className="px-4 py-2 text-right"><DevBadge pct={s.deviation_pct} /></td>
                        <td className="px-4 py-2 text-right font-mono text-slate-500 whitespace-nowrap">{fmtEur(s.planned_cost)}</td>
                        <td className="px-4 py-2 text-right font-mono font-bold text-slate-800 whitespace-nowrap">{fmtEur(s.actual_cost)}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <span className={`font-bold ${s.cost_deviation > 0 ? "text-red-500" : s.cost_deviation < 0 ? "text-emerald-600" : "text-slate-400"}`}>
                            {s.cost_deviation > 0 ? "+" : ""}{fmtEur(s.cost_deviation)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!showAllServices && (
            <p className="text-center text-xs text-slate-400 py-4">
              {data.service_comparison?.length || 0} servicios totales en el período
            </p>
          )}
        </div>
      )}
    </div>
  );
}
